import React, { useState } from 'react';
import { Calculator, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Percent, Calendar } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детальных расчетов налога на прибыль
 */
const CalculationDetails = ({ calculations, results }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!calculations || !results) {
    return null;
  }

  // Функция для форматирования сумм
  const formatAmount = (item) => {
    if (item.isPercent) {
      return `${formatNumber(item.amount, 2)}%`;
    }
    return formatCurrency(item.amount);
  };

  // Функция для отображения строк таблицы расчетов
  const renderDetailRows = (items) => {
    return items.map((item, index) => (
      <tr 
        key={`${item.name}-${index}`}
        className={`
          ${item.isBold ? 'font-semibold' : ''}
          ${item.isTotal ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}
          ${item.highlight ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
        `}
      >
        <td className="px-4 py-2">{item.name}</td>
        <td className={`px-4 py-2 text-right ${item.isBold ? 'font-semibold' : ''}`}>
          {formatAmount(item)}
        </td>
      </tr>
    ));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex justify-between items-center text-left"
      >
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация расчета
        </h3>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Детализация доходов */}
            <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                <h4 className="font-medium text-green-700 dark:text-green-300">Доходы</h4>
              </div>
              <table className="w-full">
                <tbody>
                  {renderDetailRows(calculations.incomeDetails)}
                </tbody>
              </table>
            </div>

            {/* Детализация расходов */}
            <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-red-50 dark:bg-red-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
                <h4 className="font-medium text-red-700 dark:text-red-300">Расходы</h4>
              </div>
              <table className="w-full">
                <tbody>
                  {renderDetailRows(calculations.expenseDetails)}
                </tbody>
              </table>
            </div>

            {/* Детализация применения убытков прошлых лет */}
            {calculations.deductionDetails && calculations.deductionDetails.length > 0 && (
              <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                  <TrendingDown className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <h4 className="font-medium text-blue-700 dark:text-blue-300">Учет убытков прошлых лет</h4>
                </div>
                <table className="w-full">
                  <tbody>
                    {renderDetailRows(calculations.deductionDetails)}
                  </tbody>
                </table>
              </div>
            )}

            {/* Детализация налога */}
            <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <Percent className="w-4 h-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                <h4 className="font-medium text-indigo-700 dark:text-indigo-300">Расчет налога</h4>
              </div>
              <table className="w-full">
                <tbody>
                  {renderDetailRows(calculations.taxDetails)}
                </tbody>
              </table>
            </div>

            {/* Детализация дивидендов */}
            {calculations.dividendDetails && calculations.dividendDetails.length > 0 && (
              <div className="border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-amber-50 dark:bg-amber-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                  <Percent className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2" />
                  <h4 className="font-medium text-amber-700 dark:text-amber-300">Расчет налога на дивиденды</h4>
                </div>
                <table className="w-full">
                  <tbody>
                    {renderDetailRows(calculations.dividendDetails)}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* График авансовых платежей */}
          {calculations.advancePaymentsSchedule && calculations.advancePaymentsSchedule.length > 0 && (
            <div className="mt-6 border rounded-lg border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                <h4 className="font-medium text-blue-700 dark:text-blue-300">График авансовых платежей</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Период</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Налогооблагаемая база</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Сумма налога</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Срок уплаты</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {calculations.advancePaymentsSchedule.map((payment, index) => (
                      <tr key={`payment-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-2 whitespace-nowrap">{payment.period}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(payment.taxableIncome)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(payment.taxAmount)}</td>
                        <td className="px-4 py-2">{payment.paymentDate}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold">
                      <td className="px-4 py-2">Итого</td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(
                          calculations.advancePaymentsSchedule.reduce((sum, payment) => sum + payment.taxableIncome, 0)
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(
                          calculations.advancePaymentsSchedule.reduce((sum, payment) => sum + payment.taxAmount, 0)
                        )}
                      </td>
                      <td className="px-4 py-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Информационная сноска */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Примечание:</span> Данный расчет является предварительным и служит для ознакомительных целей. 
              Точные значения могут отличаться в зависимости от конкретных условий и деталей учетной политики организации. 
              Для официальной налоговой отчетности необходимо проконсультироваться с квалифицированным бухгалтером или налоговым специалистом.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculationDetails;